import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";

import type { DataFullstoryId } from "@citrine/types/full-story";

import { QuerySuspense } from "../query-suspense";

import { VirtualFooter, VirtualItem } from "./components";
import * as sx from "./library.sx";

const VIRTUAL_COMPONENTS = {
  Footer: VirtualFooter,
  Item: VirtualItem,
};

type ItemContent<Data = unknown> = (
  index: number,
  data: Data
) => React.ReactNode;

interface BaseLibraryProps<Data = unknown> {
  "data-testid"?: string;
  "data-fullstoryid"?: DataFullstoryId;
  itemContent: ItemContent<Data>;
  emptyContent?: React.ReactNode;
  className?: string;
}

interface VirtualLibraryProps<Data = unknown> extends BaseLibraryProps<Data> {
  query: UseInfiniteQueryResult<InfiniteData<Data[]>>;
}

function VirtualLibrary<Data = unknown>({
  query,
  itemContent,
  emptyContent,
  ...props
}: VirtualLibraryProps<Data>) {
  const data = useMemo(
    () => query.data?.pages.flat() ?? [],
    [query.data?.pages]
  );

  const endReached = useCallback(() => query.fetchNextPage(), [query]);

  return data.length ? (
    <sx.VirtualWrapper {...props}>
      <Virtuoso
        components={VIRTUAL_COMPONENTS}
        data={data}
        endReached={endReached}
        overscan={60}
        context={query}
        itemContent={itemContent}
        useWindowScroll
      />
    </sx.VirtualWrapper>
  ) : (
    <>{emptyContent}</>
  );
}

interface StaticLibraryProps<Data = unknown> extends BaseLibraryProps<Data> {
  data: Data[] | undefined;
}

function StaticLibrary<Data = unknown>({
  data,
  itemContent,
  emptyContent,
  ...props
}: StaticLibraryProps<Data>) {
  return (
    <sx.StaticWrapper {...props}>
      {data?.length
        ? data?.map((item, index) => itemContent(index, item))
        : emptyContent}
    </sx.StaticWrapper>
  );
}

export type LibraryProps<Data = unknown> = BaseLibraryProps<Data> &
  (
    | {
        data: Data[];
        query?: undefined;
      }
    | {
        data?: undefined;
        query:
          | UseQueryResult<Data[]>
          | UseInfiniteQueryResult<InfiniteData<Data[]>>;
      }
  );

export function Library<Data = unknown>(props: LibraryProps<Data>) {
  const { query } = props;
  return query ? (
    <QuerySuspense
      queries={[query]}
      render={([data]) =>
        "fetchNextPage" in query ? (
          <VirtualLibrary {...(props as VirtualLibraryProps<Data>)} />
        ) : (
          <StaticLibrary {...props} data={data as Data[]} />
        )
      }
    />
  ) : (
    <StaticLibrary {...(props as StaticLibraryProps<Data>)} />
  );
}
